import { FC, useEffect, useState } from "react";
import Table from "../../assets/components/Table/Table";
import { ColumnDef } from "@tanstack/react-table";
import "../../assets/components/Table/Table_header.scss";
import "./KeyGenerator.scss";
import { Product } from "../../models/models";
import {
    useGenerateNewLicenseListMutation,
    useGetProductsListQuery,
} from "../../redux/RTKQuery/api";
import { useAppDispatch, useAppSelector } from "../../assets/hooks/hooks";
import { uiSlicedActions } from "../../redux/ui-slice";
import Loader from "../../assets/components/Loader/Loader";
import {
    GenerateKeysList,
    LicenseKey,
    LicenseKeysCollection,
    MAX_KEYS_QUANTITY_IN_TABLE,
} from "./model";
import CopyToClipboard from "../../assets/components/CopyToClipboard/CopyToClipboard";
import KeyGeneratorForm from "./KeyGeneratorHeaderForm/KeyGeneratorHeaderForm";
import TableHeader from "../../assets/components/Table/TableHeader";
import PageTitle from "../../assets/components/PageTitle/PageTitle";
import { toast } from "react-toastify";
import { useNotification } from "../../assets/hooks/useNotification";
import {
    generateAttentionTableKeysText,
    generateFileWithKeysText,
    licenseGenerationNotAllowedText,
} from "../../text";
import { ActionValue } from "../../redux/notification-slice";
import { saveAs } from "file-saver";
import { hasPermission } from "../../assets/helpers/helpers";
import { Permissions } from "../../models/models";

const columns: ColumnDef<LicenseKey>[] = [
    {
        header: () => {
            return <span>#</span>;
        },
        accessorKey: "#",
        cell: ({ row }) => <span>{row.index + 1}</span>,
    },
    {
        header: ({ table }) => {
            return (
                <div className="header_coll_items">
                    <span>License Keys</span>
                    <CopyToClipboard
                        data={table.options.data}
                        text="Select All"
                        style={{ type: "link" }}
                    />
                </div>
            );
        },
        accessorKey: "License Keys",
        cell: ({ row }) => {
            return <CopyToClipboard text={row.original} />;
        },
    },
];

const KeyGenerator: FC = () => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.initializeApp);
    const loading = useAppSelector((state) => state.ui.loading);

    const [newLicenseList, setNewLicenseList] = useState<LicenseKeysCollection>(
        []
    );
    const [products, setProducts] = useState<Product[]>([]);
    const [isToastShown, setIsToastShown] = useState(false);
    const {
        AttentionMessageBox,
        ConfirmationMessageBox,
        InformationMessageBox,
    } = useNotification();

    // The page is also reachable through the admin/user role fallback in
    // ROUTE_ACCESS.KEY_GENERATOR, so being here does not mean the permission is
    // granted — without it no quantity may be generated at all.
    const canGenerate = hasPermission(
        user.permissions,
        Permissions.LICENSE_GENERATE
    );

    // Returns the dialog result when the user may not generate at all, so
    // callers can bail out before hitting the API.
    const rejectWithoutGeneratePermission = async (): Promise<
        ActionValue | undefined
    > => {
        if (canGenerate) {
            return undefined;
        }

        return await InformationMessageBox({
            textContent: licenseGenerationNotAllowedText(),
        });
    };

    const {
        data: productsData,
        isLoading: isLoadingProducts,
        isSuccess: isSuccessLoadProducts,
        isError: isErrorLoadProducts,
    } = useGetProductsListQuery(
        { pageNumber: 1, pageSize: 30 },
        { refetchOnFocus: true }
    );

    const [
        generateNewLicenseList,
        {
            data,
            isLoading: isLoadingGenerateList,
            isSuccess: isSuccessGenerateList,
            isError: isErrorGenerateList,
        },
    ] = useGenerateNewLicenseListMutation();

    const generateFileHandler = async (
        values: GenerateKeysList
    ): Promise<void | ActionValue> => {
        const rejected = await rejectWithoutGeneratePermission();

        if (rejected !== undefined) {
            return rejected;
        }

        const result = await ConfirmationMessageBox({
            textContent: generateFileWithKeysText(values.keysQuantity),
        });

        console.log(`
            Generate file with keys
            keysQuantity: ${values.keysQuantity}
            confirm: ${result}
        `);

        if (result === "yes") {
            try {
                const response = await generateNewLicenseList(values).unwrap();

                if ("blob" in response) {
                    saveAs(response.blob, response.filename);
                }
            } catch (error) {
                console.log("Error generating file with keys:", error);
            }
        }

        return Promise.resolve();
    };

    const generateTableHandler = async (
        values: GenerateKeysList
    ): Promise<void | ActionValue> => {
        // Checked before the table limit — pointing a user who may not generate
        // at all at "Generate to File" would only lead to the same refusal.
        const rejected = await rejectWithoutGeneratePermission();

        if (rejected !== undefined) {
            return rejected;
        }

        if (values.keysQuantity > MAX_KEYS_QUANTITY_IN_TABLE) {
            return await AttentionMessageBox({
                textContent: generateAttentionTableKeysText(
                    MAX_KEYS_QUANTITY_IN_TABLE
                ),
                actionButtons: [
                    {
                        name: "ok",
                        valueAfterClick: "ok",
                        role: "primary",
                        onClick: () => {
                            console.log("Button 'ok' clicked!");
                        },
                    },
                ],
            });
        }

        const data = await generateNewLicenseList(values).unwrap();

        if (data instanceof Array) {
            setNewLicenseList(data);
        }
    };

    useEffect(() => {
        if (isSuccessLoadProducts) {
            setProducts(productsData.products);
        }

        if (isErrorLoadProducts) {
            toast.error("Error loading products");
        }
    }, [isSuccessLoadProducts, isErrorLoadProducts, productsData]);

    useEffect(() => {
        if (isSuccessGenerateList) {
            if (!isToastShown) {
                toast.success("License keys generated successfully");
                setIsToastShown(true);
            }
        }

        if (isErrorGenerateList) {
            toast.error("Error generating license keys");
        }
    }, [isSuccessGenerateList, isErrorGenerateList, data, isToastShown]);

    useEffect(() => {
        const loadingGlobal = isLoadingProducts || isLoadingGenerateList;
        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden")
        );
    }, [isLoadingProducts, isLoadingGenerateList, dispatch]);

    return (
        <div className="page_with_table">
            <Loader loading={loading} />
            <PageTitle text="Key Generator" />

            {products.length ? (
                <div>
                    <TableHeader title="Key Generator" />
                    <KeyGeneratorForm
                        user={{
                            id: user.userId,
                            name: user.userName,
                            role: user.role,
                            permissions: user.permissions,
                        }}
                        products={products}
                        generateFileHandler={generateFileHandler}
                        generateTableHandler={generateTableHandler}
                    />
                </div>
            ) : null}

            {newLicenseList.length ? (
                <div className="table_scroll_wrapper">
                    <Table<LicenseKey>
                        data={newLicenseList}
                        columns={columns}
                        className="key_generator"
                    />
                </div>
            ) : null}
        </div>
    );
};

export default KeyGenerator;
